import { SettingOutlined } from "@ant-design/icons";
import { Badge, Button, Form, Modal } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { BeaconConfigFile, DynamicModel, useConfigureBeacons } from "../../api";
import { BeaconConfigFormBulk } from "./BeaconConfigFormBulk";
import { UploadFile } from "../UploadFile";
import { DownloadFile } from "../DownloadFile";

interface BeaconConfigModalBulkProps {
  selectedBeaconSerials: string[];
}

export function BeaconConfigModalBulk({
  selectedBeaconSerials,
}: BeaconConfigModalBulkProps) {
  const { t } = useTranslation();

  const [form] = Form.useForm();
  const [isOpen, setIsOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavedDisabled, setIsSavedDisabled] = useState(false);
  const [formValues, setFormValues] = useState<BeaconConfigFile | null>(null);

  const configure = useConfigureBeacons();

  const validateFormDataReadyForSave = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (errorInfo) {
      return false;
    }
  };

  const success = () => {
    Modal.success({
      title: t("beacon:configure-success-title"),
      content: t("beacon:configure-success-body"),
      onOk() {
        resetModal();
        setIsOpen(false);
      },
    });
  };

  const error = () => {
    Modal.error({
      title: t("error"),
      content: t("beacon:configure-failed-title"),
    });
  };

  const saveConfiguration = async () => {
    const values = form.getFieldsValue();

    const desiredConfig = {
      activityInterval: Number(values["activityInterval"]),
      inactivityInterval: Number(values["inactivityInterval"]),
      heartbeat: Number(values["heartbeat"]),
      ignoreMotion: Number(values["ignoreMotion"]),
      accelerometer: {
        duration: Number(values["accelerometer.duration"]),
        threshold: Number(values["accelerometer.threshold"]),
        x: Boolean(values["accelerometer.x"]),
        y: Boolean(values["accelerometer.y"]),
        z: Boolean(values["accelerometer.z"]),
      },
      positioning: {
        heartbeat: Number(values["positioning.heartbeat"]),
        ttffTimeout: Number(values["positioning.ttffTimeout"]),
        ttffRetries: Number(values["positioning.ttffRetries"]),
        fixQualityTimeout: Number(values["positioning.fixQualityTimeout"]),
        dynamicModel: Number(values["positioning.dynamicModel"]),
      },
      rtk: {
        protocol: "ntrip-v2",
        credentials: {
          host: values["rtk.credentials.host"],
          port: Number(values["rtk.credentials.port"]),
          mountpoint: values["rtk.credentials.mountpoint"],
          user: values["rtk.credentials.user"],
          password: values["rtk.credentials.password"],
        },
      },
    };

    if (await validateFormDataReadyForSave()) {
      setIsSaving(true);
      const result = await configure({
        beaconSerials: selectedBeaconSerials,
        configuration: desiredConfig,
      });
      setIsSaving(false);

      if (result.error) error();
      else {
        setFormValues(null);
        success();
      }
    }
  };

  const resetModal = () => {
    form.resetFields();
    setFormValues(null);
    setIsSaving(false);
    setIsOpen(false);
  };

  const setConfigData = async (data: BeaconConfigFile) => {
    // Ensure dynamicModel from uploaded file is valid, if not default to STATIONARY
    if (
      data["positioning.dynamicModel"] !== undefined &&
      !Object.values(DynamicModel).includes(
        data["positioning.dynamicModel"] as DynamicModel
      )
    ) {
      data["positioning.dynamicModel"] = DynamicModel.STATIONARY;
    }

    form.setFieldsValue(data);

    setFormValues(data);
  };

  const handleValuesChange = (
    changedValues: BeaconConfigFile,
    allValues: BeaconConfigFile
  ) => {
    setFormValues(allValues);
  };

  return (
    <>
      <Badge count={selectedBeaconSerials.length}>
        <Button
          color="default"
          disabled={selectedBeaconSerials.length === 0}
          onClick={() => {
            setIsOpen(true);
          }}
        >
          <SettingOutlined />
          {t("beacon:configure-selected", {
            count: selectedBeaconSerials.length,
          })}
        </Button>
      </Badge>
      <Modal
        width="800px"
        style={{
          top: Math.max(16, window.innerHeight * 0.15 - 77),
          minWidth: "800px",
        }}
        bodyStyle={{
          overflowY: "auto",
          height: "70vh",
        }}
        title={t("beacon:configuration-title-plural", {
          ids: selectedBeaconSerials.join(", "),
        })}
        open={isOpen}
        onCancel={() => resetModal()}
        onOk={saveConfiguration}
        confirmLoading={isSaving}
        destroyOnClose
        footer={
          <BeaconConfigurationFooter
            downloadFileName={selectedBeaconSerials.join("_")}
            formValues={formValues ?? form.getFieldsValue()}
            isSavedDisabled={isSavedDisabled}
            isSaving={isSaving}
            setConfigData={setConfigData}
            saveConfiguration={saveConfiguration}
            resetModal={resetModal}
          />
        }
      >
        <BeaconConfigFormBulk form={form} onValuesChange={handleValuesChange} />
      </Modal>
    </>
  );
}

interface BeaconConfigurationFooterProps {
  downloadFileName: string;
  formValues: BeaconConfigFile;
  isSavedDisabled: boolean;
  isSaving: boolean;
  saveConfiguration: () => void;
  resetModal: () => void;
  setConfigData: (data: BeaconConfigFile) => void;
}

export function BeaconConfigurationFooter({
  downloadFileName,
  formValues,
  isSavedDisabled,
  isSaving,
  saveConfiguration,
  resetModal,
  setConfigData,
}: BeaconConfigurationFooterProps) {
  const { t } = useTranslation();

  return (
    <div
      key="footer-container"
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
      }}
    >
      <div style={{ display: "flex", gap: "8px" }}>
        <UploadFile setConfigData={setConfigData} />
        <DownloadFile
          fileContent={JSON.stringify(formValues, null, 2)}
          fileName={`config-${downloadFileName}.json`}
          fileType="application/json"
        />
      </div>
      <div key="button-group" style={{ display: "flex", gap: "8px" }}>
        <Button key="cancel-button" onClick={() => resetModal()}>
          {t("translations:cancel")}
        </Button>
        <Button
          key="save-button"
          type="primary"
          onClick={saveConfiguration}
          disabled={isSavedDisabled}
          loading={isSaving}
        >
          {t("translations:save")}
        </Button>
      </div>
    </div>
  );
}
